import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Download, ZoomIn, X } from "lucide-react";
import PlotBoard, { PlotLegend } from "@/components/PlotBoard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const Chapter = ({ n }) => <span className="font-serif-display text-sm italic text-[var(--gold)]">{n}</span>;

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay }}
    className={className}
  >
    {children}
  </motion.div>
);

export default function PlotAvailability() {
  const [plots, setPlots] = useState([]);
  const [zoom, setZoom] = useState(false);
  const [pulse, setPulse] = useState(() => new Set());
  const statusRef = useRef({});

  const applyPlots = (data, detectFlips) => {
    if (detectFlips) {
      const newlySold = data.filter(
        (p) => p.status === "sold" && statusRef.current[p.number] === "available"
      ).map((p) => p.number);
      if (newlySold.length) {
        setPulse(new Set(newlySold));
        newlySold.forEach((n) => toast.success(`Plot ${n} has just been sold! 🎉`));
        setTimeout(() => setPulse(new Set()), 2600);
      }
    }
    statusRef.current = Object.fromEntries(data.map((p) => [p.number, p.status]));
    setPlots(data);
  };

  useEffect(() => {
    let active = true;
    const fetchPlots = (detect) =>
      axios.get(`${API}/plots`).then((r) => { if (active) applyPlots(r.data, detect); }).catch(() => {});
    fetchPlots(false);
    const id = setInterval(() => fetchPlots(true), 10000);
    return () => { active = false; clearInterval(id); };
    // eslint-disable-next-line
  }, []);

  const available = plots.filter((p) => p.status === "available").length;
  const reserved = plots.filter((p) => p.status === "reserved").length;
  const sold = plots.filter((p) => p.status === "sold").length;

  return (
    <section id="master-plan" className="mx-auto max-w-7xl px-5 py-24 md:px-10 md:py-36">
      <Reveal>
        <p className="mb-6 text-xs uppercase tracking-[0.4em] text-[var(--gold)]">
          <Chapter n="05" /> — Master Plan & Availability
        </p>
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <h2 className="max-w-2xl font-serif-display text-4xl leading-tight tracking-tight text-[var(--ink)] md:text-5xl">
            See what's available. Pick your plot with confidence.
          </h2>
          <a
            href="/nova-royale-brochure.pdf"
            download
            className="inline-flex shrink-0 items-center gap-2 rounded-sm border border-[var(--ink)] px-6 py-3 text-xs uppercase tracking-[0.2em] text-[var(--ink)] transition-colors duration-300 hover:bg-[var(--ink)] hover:text-[var(--cream)]"
            data-testid="download-brochure-plan"
          >
            <Download size={16} /> Download Brochure
          </a>
        </div>
      </Reveal>

      <Reveal delay={0.1} className="mt-12">
        <div className="group relative overflow-hidden rounded-sm border border-[var(--cream-3)] bg-white">
          <button
            type="button"
            onClick={() => setZoom(true)}
            className="block w-full cursor-zoom-in"
            aria-label="Open full layout plan"
            data-testid="plan-image-btn"
          >
            <img src="/nova-royale-layout.png" alt="Nova Royale sanctioned layout plan" className="w-full object-contain" />
          </button>
          <button
            onClick={() => setZoom(true)}
            className="absolute bottom-3 right-3 flex items-center gap-2 rounded-sm bg-[var(--gold)] px-3.5 py-2.5 text-xs uppercase tracking-[0.15em] text-[#1C2427] shadow-lg transition-transform hover:-translate-y-0.5 md:bottom-4 md:right-4"
            data-testid="zoom-plan-btn"
          >
            <ZoomIn size={15} /> <span className="hidden sm:inline">Tap to </span>Zoom
          </button>
        </div>
        <p className="mt-3 text-center text-xs text-[var(--ink-soft)] md:hidden">Tap the plan to open it full-screen and pinch/scroll to explore.</p>
      </Reveal>

      <Reveal delay={0.15} className="mt-14">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="font-serif-display text-2xl text-[var(--ink)]">Live Plot Availability</h3>
          <PlotLegend available={available} reserved={reserved} sold={sold} />
        </div>
        {plots.length === 0 ? (
          <p className="text-[var(--ink-soft)]">Loading availability…</p>
        ) : (
          <PlotBoard plots={plots} pulseNumbers={pulse} />
        )}
        <p className="mt-6 text-sm text-[var(--ink-soft)]">
          Availability is updated in real time by our team. Found a plot you like?{" "}
          <button
            onClick={() => document.querySelector("#enquiry")?.scrollIntoView({ behavior: "smooth" })}
            className="link-underline text-[var(--gold-hover)]"
            data-testid="board-enquire-link"
          >
            Enquire now to reserve it.
          </button>
        </p>
      </Reveal>

      <AnimatePresence>
        {zoom && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/90 p-4"
            onClick={() => setZoom(false)}
            data-testid="plan-zoom-overlay"
          >
            <button className="absolute right-5 top-5 z-10 text-white" onClick={() => setZoom(false)} data-testid="plan-zoom-close">
              <X size={30} />
            </button>
            <div className="h-full w-full overflow-auto" style={{ WebkitOverflowScrolling: "touch" }} onClick={(e) => e.stopPropagation()}>
              <img
                src="/nova-royale-layout.png"
                alt="Nova Royale layout plan enlarged"
                className="mx-auto max-w-none"
                style={{ width: "1600px" }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
